import AuthInput from '@/@shared/components/forms/AuthInput';
import SubmitButton from '@/@shared/components/primitives/Button/SubmitButton';
import { useTransAuth } from '@/@shared/hooks/useTranslations';
import type { PannelProps } from '@/app/[locale]/(auth)/signup/(funnel)/PannelType';
import styles from '@/app/[locale]/(auth)/signup/(funnel)/RegisterComp.module.scss';
import { clc } from '@/legacy/components/base/utils';
import { errorStyle } from '@/legacy/components/input/inputUtil';
import type { SignUpType } from '@/model/zod/authZod';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

type VerifyEmailProps = PannelProps & {
	onVerify: (code: string) => Promise<boolean>;
	onResend: (email: string) => Promise<boolean>;
	isVerifying?: boolean;
};

const VerifyEmail = ({ locale, onNext, onVerify, onResend, isVerifying }: VerifyEmailProps) => {
	const t = useTransAuth('signup.emailVerify');

	const [verified, setVerified] = useState(false);
	const [resendMessage, setResendMessage] = useState<string | null>(null);

	const {
		register,
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useFormContext<SignUpType>();

	const verifyEmailRegister = register('verifyEmail', {
		required: true,
	});

	const a = register('verifyStatus', {
		required: true,
	});

	// watch를 사용하여 리액티브하게 값 감시 (복붙 대응)
	const verifyEmailValue = watch('verifyEmail');

	const handleVerifyEmailClick = async () => {
		if (!errors.verifyEmail) {
			const authCode = getValues('verifyEmail');

			if (!authCode) {
				return;
			}

			const success = await onVerify(authCode);
			if (success) {
				setVerified(true);
				onNext();
			}
		}
	};

	const resendEmail = async () => {
		const emailValue = getValues('getEmail');

		if (emailValue) {
			const success = await onResend(emailValue);
			if (success) {
				// 성공 시: 입력 필드 초기화 및 메시지 표시
				setValue('verifyEmail', '');
				setResendMessage(t('resent'));

				// 3초 후 메시지 제거
				setTimeout(() => {
					setResendMessage(null);
				}, 3000);
			}
		}
	};

	const maxLengthCheck = (object: any) => {
		if (object.value.length > object.maxLength) {
			object.value = object.value.slice(0, object.maxLength);
		}
	};

	return (
		<>
			<div className={clc(styles.title, styles.under20)}>
				<div className={styles.line2}>
					<span>{t('description')}</span>
				</div>
			</div>
			<AuthInput
				className={errorStyle(errors.verifyEmail?.message?.toString())}
				errors={errors}
				keyName={'verifyEmail'}
				label={t('placeholder')}
				required
				{...verifyEmailRegister}
				autoComplete="off"
				getValues={getValues}
				maxLength={6}
				onInput={(e) => maxLengthCheck(e.target)}
				placeholder={t('placeholder')}
				setValue={setValue}
				type="number"
				verified={verified}
				onEnterPress={handleVerifyEmailClick}
			/>
			{resendMessage && (
				<div className={styles.notification}>
					<span>{resendMessage}</span>
				</div>
			)}
			<div className={styles.resendArea}>
				<div className={styles.resend} onClick={resendEmail}>
					<span>{t('resend')}</span>
				</div>
			</div>
			<div className={styles.submitArea}>
				<SubmitButton
					loading={isVerifying || false}
					isValid={!errors.verifyEmail && Boolean(verifyEmailValue)}
					onClick={handleVerifyEmailClick}
					action={t('submit')}
					loadingText={t('loading')}
					type="button"
					fullWidth
				/>
			</div>
		</>
	);
};

export default VerifyEmail;
